// Real-time Context for React
// Manages real-time Firebase listeners across the application

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { auth } from '../firebase/config';
import { createMissingUserProfile } from '../utils/createMissingUserProfile';

import {
  listenToUserDashboard,
  listenToUserTestResults,
  listenToUserNotes,
  listenToUserDoubts,
  listenToUserActivities,
  listenToUserNotifications,
  listenToUserPreferences,
  listenToUserBookmarks,
  listenToUserCourses,
  listenToAllUsers,
  listenToAllTestResults,
  listenToAllQuestions,
  listenToAllCourses,
  listenToAllDoubts,
  listenToAnalytics,
  listenToAdminActions,
  cleanupAllListeners,
  cleanupUserListeners,
  logAdminAction
} from '../firebase/realtime';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { signOut } from 'firebase/auth';
import { debugUserData, debugAuthMethod } from '../utils/debugAuth';

const RealtimeContext = createContext();

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

export const RealtimeProvider = ({ children }) => {
  const [userData, setUserData] = useState({
    dashboard: null,
    testResults: [],
    notes: [],
    doubts: [],
    activities: [],
    notifications: [],
    preferences: null,
    bookmarks: [],
    userCourses: []
  });
  
  const [adminData, setAdminData] = useState({
    users: [],
    testResults: [],
    questions: [],
    courses: [],
    doubts: [],
    analytics: null,
    adminActions: []
  });

  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [listenersInitialized, setListenersInitialized] = useState(false);

  // Initialize user listeners with MINIMAL initial load - OPTIMIZED
  const initializeUserListeners = useCallback(async (userId) => {
    if (!userId) return;

    try {
      // OPTIMIZATION: Only load user profile on login, nothing else
      // All other data will be loaded on-demand when user navigates to specific pages
      
      // Load preferences (essential for UI) - but use cache first
      const cachedPrefs = localStorage.getItem(`prefs_${userId}`);
      const cacheTimestamp = localStorage.getItem(`prefs_${userId}_ts`);
      const isCacheValid = cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < 600000; // 10 min
      
      if (cachedPrefs && isCacheValid) {
        setUserData(prev => ({ ...prev, preferences: JSON.parse(cachedPrefs) }));
      }

      // Load preferences with listener (but cache the result)
      const preferencesUnsubscribe = listenToUserPreferences(userId, (result) => {
        if (result.success) {
          setUserData(prev => ({ ...prev, preferences: result.data }));
          // Cache preferences
          localStorage.setItem(`prefs_${userId}`, JSON.stringify(result.data));
          localStorage.setItem(`prefs_${userId}_ts`, Date.now().toString());
        }
      });

      // Load exam preferences from cache or fetch once (no listener)
      const loadExamPreferences = async () => {
        try {
          const cachedExamPrefs = localStorage.getItem(`examPrefs_${userId}`);
          const examCacheTs = localStorage.getItem(`examPrefs_${userId}_ts`);
          const isExamCacheValid = examCacheTs && (Date.now() - parseInt(examCacheTs)) < 600000;
          
          if (cachedExamPrefs && isExamCacheValid) {
            const examPrefsData = JSON.parse(cachedExamPrefs);
            setUserData(prev => ({
              ...prev,
              examPreferences: examPrefsData,
              preferences: {
                ...prev.preferences,
                selectedExams: examPrefsData.selectedExams || prev.preferences?.selectedExams || [],
                activeExam: examPrefsData.activeExam || prev.preferences?.activeExam || null
              }
            }));
            return;
          }

          const { getUserExamPreferences } = await import('../firebase/collections');
          const examPrefsResult = await getUserExamPreferences(userId);
          if (examPrefsResult.success && examPrefsResult.data) {
            setUserData(prev => ({
              ...prev,
              examPreferences: examPrefsResult.data,
              preferences: {
                ...prev.preferences,
                selectedExams: examPrefsResult.data.selectedExams || prev.preferences?.selectedExams || [],
                activeExam: examPrefsResult.data.activeExam || prev.preferences?.activeExam || null
              }
            }));
            // Cache exam preferences
            localStorage.setItem(`examPrefs_${userId}`, JSON.stringify(examPrefsResult.data));
            localStorage.setItem(`examPrefs_${userId}_ts`, Date.now().toString());
          }
        } catch (error) {
          console.error('Error loading exam preferences:', error);
        }
      };
      
      loadExamPreferences();

      // REMOVED: dashboardUnsubscribe - load on demand
      // REMOVED: testResultsUnsubscribe - load on demand
      // REMOVED: notificationsUnsubscribe - load on demand

      // Store unsubscribe functions for cleanup
      return () => {
        preferencesUnsubscribe();
      };
    } catch (error) {
      setConnectionStatus('error');
    }
  }, []);

  // OPTIMIZED: Load data on-demand only (no automatic loading)
  const loadAdditionalUserData = useCallback(async (userId, dataType) => {
    if (!userId) return;

    try {
      // Load specific data type only when requested
      let unsubscribe = null;

      switch (dataType) {
        case 'notes':
          // Check cache first
          const cachedNotes = localStorage.getItem(`notes_${userId}`);
          const notesCacheTs = localStorage.getItem(`notes_${userId}_ts`);
          if (cachedNotes && notesCacheTs && (Date.now() - parseInt(notesCacheTs)) < 300000) {
            setUserData(prev => ({ ...prev, notes: JSON.parse(cachedNotes) }));
            return;
          }
          unsubscribe = listenToUserNotes(userId, 20, (result) => {
            if (result.success) {
              setUserData(prev => ({ ...prev, notes: result.data }));
              localStorage.setItem(`notes_${userId}`, JSON.stringify(result.data));
              localStorage.setItem(`notes_${userId}_ts`, Date.now().toString());
            }
          });
          break;

        case 'doubts':
          const cachedDoubts = localStorage.getItem(`doubts_${userId}`);
          const doubtsCacheTs = localStorage.getItem(`doubts_${userId}_ts`);
          if (cachedDoubts && doubtsCacheTs && (Date.now() - parseInt(doubtsCacheTs)) < 300000) {
            setUserData(prev => ({ ...prev, doubts: JSON.parse(cachedDoubts) }));
            return;
          }
          unsubscribe = listenToUserDoubts(userId, 20, (result) => {
            if (result.success) {
              setUserData(prev => ({ ...prev, doubts: result.data }));
              localStorage.setItem(`doubts_${userId}`, JSON.stringify(result.data));
              localStorage.setItem(`doubts_${userId}_ts`, Date.now().toString());
            }
          });
          break;

        case 'testResults':
          const cachedResults = localStorage.getItem(`testResults_${userId}`);
          const resultsCacheTs = localStorage.getItem(`testResults_${userId}_ts`);
          if (cachedResults && resultsCacheTs && (Date.now() - parseInt(resultsCacheTs)) < 300000) {
            setUserData(prev => ({ ...prev, testResults: JSON.parse(cachedResults) }));
            return;
          }
          unsubscribe = listenToUserTestResults(userId, 10, (result) => {
            if (result.success) {
              setUserData(prev => ({ ...prev, testResults: result.data }));
              localStorage.setItem(`testResults_${userId}`, JSON.stringify(result.data));
              localStorage.setItem(`testResults_${userId}_ts`, Date.now().toString());
            }
          });
          break;

        case 'notifications':
          unsubscribe = listenToUserNotifications(userId, (result) => {
            if (result.success) {
              setUserData(prev => ({ ...prev, notifications: result.data }));
            }
          });
          break;

        default:
          break;
      }

      return unsubscribe;
    } catch (error) {
      console.error('Error loading additional user data:', error);
    }
  }, []);

  // Initialize admin listeners with reduced initial load
  const initializeAdminListeners = useCallback(async () => {
    try {
      // Start with essential admin data only
      const usersUnsubscribe = listenToAllUsers((result) => {
        if (result.success) {
          setAdminData(prev => ({ ...prev, users: result.data }));
        }
      });

      // Load analytics (essential for admin dashboard)
      const analyticsUnsubscribe = listenToAnalytics((result) => {
        if (result.success) {
          setAdminData(prev => ({ ...prev, analytics: result.data }));
        }
      });

      // Store unsubscribe functions for cleanup
      return () => {
        usersUnsubscribe();
        analyticsUnsubscribe();
      };
    } catch (error) {
      setConnectionStatus('error');
    }
  }, []);

  // Lazy load additional admin data
  const loadAdditionalAdminData = useCallback(async () => {
    try {
      // Load test results
      const testResultsUnsubscribe = listenToAllTestResults((result) => {
        if (result.success) {
          setAdminData(prev => ({ ...prev, testResults: result.data }));
        }
      });

      // Load questions
      const questionsUnsubscribe = listenToAllQuestions((result) => {
        if (result.success) {
          setAdminData(prev => ({ ...prev, questions: result.data }));
        }
      });

      // Load courses
      const coursesUnsubscribe = listenToAllCourses((result) => {
        if (result.success) {
          setAdminData(prev => ({ ...prev, courses: result.data }));
        }
      });

      // Load doubts
      const doubtsUnsubscribe = listenToAllDoubts((result) => {
        if (result.success) {
          setAdminData(prev => ({ ...prev, doubts: result.data }));
        }
      });

      // Load admin actions
      const adminActionsUnsubscribe = listenToAdminActions((result) => {
        if (result.success) {
          setAdminData(prev => ({ ...prev, adminActions: result.data }));
        }
      });

      // Store additional unsubscribe functions
      return () => {
        testResultsUnsubscribe();
        questionsUnsubscribe();
        coursesUnsubscribe();
        doubtsUnsubscribe();
        adminActionsUnsubscribe();
      };
    } catch (error) {
      // Error loading additional admin data
    }
  }, []);

  // Cleanup listeners
  const cleanupListeners = useCallback(() => {
    cleanupAllListeners();
    setConnectionStatus('disconnected');
  }, []);

  // Log admin action with real-time feedback
  const logAdminActionWithFeedback = useCallback(async (action, details) => {
    try {
      const result = await logAdminAction(auth.currentUser?.uid || 'system', action, details);
      if (result.success) {
        return result;
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error };
    }
  }, []);

  // Refresh user data
  const refreshUserData = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      setConnectionStatus('refreshing');
      
      // Reinitialize user listeners
      const cleanup = await initializeUserListeners(currentUser.uid);
      
      setConnectionStatus('connected');
      return cleanup;
    } catch (error) {
      setConnectionStatus('error');
    }
  }, [currentUser?.uid, initializeUserListeners]);

  // Refresh admin data
  const refreshAdminData = useCallback(async () => {
    try {
      setConnectionStatus('refreshing');
      
      // Reinitialize admin listeners
      const cleanup = await initializeAdminListeners();
      
      setConnectionStatus('connected');
      return cleanup;
    } catch (error) {
      setConnectionStatus('error');
    }
  }, [initializeAdminListeners]);

  // Get specific user data
  const getUserData = useCallback((dataType) => {
    return userData[dataType] || null;
  }, [userData]);

  // Get specific admin data
  const getAdminData = useCallback((dataType) => {
    return adminData[dataType] || null;
  }, [adminData]);

  // Subscribe to specific data type
  const subscribeToData = useCallback((dataType, callback) => {
    // Set up interval to check for data changes
    const interval = setInterval(() => {
      const data = dataType.startsWith('admin') 
        ? getAdminData(dataType.replace('admin', ''))
        : getUserData(dataType);
      
      callback(data);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [getUserData, getAdminData]);

  // Load user profile
  const loadUserProfile = useCallback(async (userId, retryCount = 0) => {
    if (!userId) {
      setUserProfile(null);
      return null;
    }
    
    try {
      // Use createMissingUserProfile instead of getUserProfile to handle
      // both creation and automatic linking to coaching/institution
      const result = await createMissingUserProfile(auth.currentUser);
      if (result.success) {
        setUserProfile(result.data);
        return result.data;
      } else {
        // If it failed but it's a transient error, retry
        const isTransient = result.error !== 'User not found' && result.error?.message !== 'User not found';
        if (isTransient && retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000;
          console.log(`🔄 Profile load failed (transient), retrying in ${delay}ms...`, result.error);
          await new Promise(resolve => setTimeout(resolve, delay));
          return loadUserProfile(userId, retryCount + 1);
        }
        
        // If it's truly not found or we've exhausted retries
        if (!isTransient) {
          console.log('ℹ️ Profile not found, creation should have happened or failed.');
        }
        setUserProfile(null);
        return null;
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return loadUserProfile(userId, retryCount + 1);
      }
      setUserProfile(null);
      return null;
    }
  }, []);

  // Subscribe to user profile changes in real-time
  useEffect(() => {
    if (!currentUser?.uid) {
      setUserProfile(null);
      return;
    }
    
    const userProfileRef = doc(db, COLLECTIONS.USERS, currentUser.uid);
    const unsubscribe = onSnapshot(userProfileRef, async (docSnap) => {
      if (docSnap.exists()) {
        const profileData = docSnap.data();
        setUserProfile(profileData);
        
        // If profile has exam preferences, ensure they're available in userData
        if (profileData.preferences?.selectedExams) {
          setUserData(prev => ({
            ...prev,
            preferences: {
              ...prev.preferences,
              selectedExams: profileData.preferences.selectedExams,
              activeExam: profileData.preferences.activeExam
            }
          }));
        }
      } else {
        setUserProfile(null);
      }
    }, (error) => {
      // Error loading profile - try loading once
      loadUserProfile(currentUser.uid);
    });

    return () => unsubscribe();
  }, [currentUser?.uid, loadUserProfile]);

  // Initialize context when user changes
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      
      if (user) {
        setConnectionStatus('connecting');
        
        // Load user profile immediately and wait for it
        console.log('⏳ RealtimeContext: Fetching profile for user:', user.email);
        const profile = await loadUserProfile(user.uid);
        
        try {
          // Initialize essential user listeners first
          const userCleanup = await initializeUserListeners(user.uid);
          
          // Initialize essential admin listeners (for admin users)
          let adminCleanup = null;
          // Check role from profile or email
          const isUserAdmin = user.email?.includes('admin') || 
                             user.email?.includes('mahstry') || 
                             (profile && (profile.role === 'admin' || profile.role === 'superadmin' || profile.role === 'founder' || profile.isAdmin));
          
          if (isUserAdmin) {
            console.log('👮 Admin detected, initializing admin listeners...');
            adminCleanup = await initializeAdminListeners();
          }
          
          setIsConnected(true);
          setConnectionStatus('connected');
          
          // CRITICAL: Ensure profile is actually available in state before final initialization
          // If profile fetch failed or is still null, we try one more time or just proceed
          // RoleBasedRedirect will catch null profiles and show spinner
          setIsInitialized(true);
          console.log('✅ RealtimeContext initialized (Profile:', profile ? 'Loaded' : 'Missing', ')');
          
          // Store cleanup functions
          return () => {
            if (userCleanup) userCleanup();
            if (adminCleanup) adminCleanup();
          };
        } catch (error) {
          setConnectionStatus('error');
          setIsConnected(false);
          setIsInitialized(true);
        }
      } else {
        // User logged out
        
        cleanupListeners();
        setIsConnected(false);
        setConnectionStatus('disconnected');
        setListenersInitialized(false);
        setUserProfile(null);
        setUserData({
          dashboard: null,
          testResults: [],
          notes: [],
          doubts: [],
          activities: [],
          notifications: [],
          preferences: null,
          bookmarks: [],
          userCourses: []
        });
        setAdminData({
          users: [],
          testResults: [],
          questions: [],
          courses: [],
          doubts: [],
          analytics: null,
          adminActions: []
        });
        setIsInitialized(true);
      }
    });

    return () => {
      unsubscribeAuth();
      cleanupListeners();
    };
  }, [initializeUserListeners, initializeAdminListeners, loadAdditionalUserData, loadAdditionalAdminData, cleanupListeners, loadUserProfile]);

  const value = {
    // Connection status
    isConnected,
    connectionStatus,
    isInitialized,
    listenersInitialized,
    
    // User data
    userData,
    getUserData,
    userProfile, // Add userProfile to context
    
    // Admin data
    adminData,
    getAdminData,
    
    // Control functions
    refreshUserData,
    refreshAdminData,
    cleanupListeners,
    logAdminAction: logAdminActionWithFeedback,
    loadAdditionalUserData,
    loadAdditionalAdminData,
    refreshUserProfile: () => loadUserProfile(currentUser?.uid), // Add refresh function
    
    // Subscription
    subscribeToData,
    
    // Current user
    currentUser,
    
    // Debug utilities
    debugUserData: () => debugUserData(currentUser?.uid),
    debugAuthMethod: () => debugAuthMethod(currentUser)
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export default RealtimeProvider;
